import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class NewsService {
  private readonly publicNewsUrl = `${environment.apiUrl}/public/news`;
  private readonly adminNewsUrl = `${environment.apiUrl}/admin/news`;
  private readonly publicTagsUrl = `${environment.apiUrl}/public/tags`;
  private readonly adminTagsUrl = `${environment.apiUrl}/admin/tags`;

  constructor(private http: HttpClient) {}

  getNews(page: number = 1, tag?: string, filter?: string, perPage?: number): Observable<any> {
    let url = `${this.publicNewsUrl}?page=${page}`;
    if (tag) {
      url += `&tag=${tag}`;
    }
    if (filter) {
      url += `&filter=${encodeURIComponent(filter)}`;
    }
    if (perPage) {
      url += `&per_page=${perPage}`;
    }
    return this.http.get<any>(url);
  }

  getLatestAnnouncement(): Observable<any> {
    return this.getNews(1, undefined, 'announcement', 1);
  }

  getNewsBySlug(slug: string): Observable<any> {
    return this.http.get<any>(`${this.publicNewsUrl}/${slug}`);
  }

  trackVisit(slug: string, visitorId: string): Observable<any> {
    const headers = new HttpHeaders({ 'X-Visitor-Id': visitorId });
    return this.http.post<any>(`${this.publicNewsUrl}/${slug}/visit`, {}, { headers });
  }

  getEngagement(slug: string): Observable<any> {
    return this.http.get<any>(`${this.publicNewsUrl}/${slug}/engagement`);
  }

  getAdminNews(
    params: {
      page?: number;
      perPage?: number;
      search?: string;
      status?: string;
      tag?: string;
      featured?: boolean | null;
      dateFrom?: string;
      dateTo?: string;
    } = {},
  ): Observable<any> {
    const query = new URLSearchParams();
    query.set('page', String(params.page ?? 1));
    query.set('per_page', String(params.perPage ?? 15));

    if (params.search?.trim()) {
      query.set('search', params.search.trim());
    }
    if (params.status?.trim()) {
      query.set('status', params.status.trim());
    }
    if (params.tag?.trim()) {
      query.set('tag', params.tag.trim());
    }
    if (params.featured !== null && params.featured !== undefined) {
      query.set('featured', params.featured ? '1' : '0');
    }
    if (params.dateFrom?.trim()) {
      query.set('date_from', params.dateFrom.trim());
    }
    if (params.dateTo?.trim()) {
      query.set('date_to', params.dateTo.trim());
    }

    return this.http.get<any>(`${this.adminNewsUrl}?${query.toString()}`);
  }

  addNews(formData: FormData): Observable<any> {
    return this.http.post(this.adminNewsUrl, formData);
  }

  updateNews(uuid: string, formData: FormData): Observable<any> {
    return this.http.post(`${this.adminNewsUrl}/${uuid}`, formData);
  }

  deleteNews(uuid: string): Observable<any> {
    return this.http.delete(`${this.adminNewsUrl}/${uuid}`);
  }

  bulkDeleteNews(uuids: string[]): Observable<any> {
    return this.http.post(`${this.adminNewsUrl}/bulk-delete`, { uuids });
  }

  updateOrder(items: Array<{ uuid: string; display_order: number }>): Observable<any> {
    return this.http.post(`${this.adminNewsUrl}/update-order`, { items });
  }

  getTags(): Observable<any[]> {
    return this.http.get<any[]>(this.publicTagsUrl);
  }

  getAdminTags(search?: string, perPage?: number): Observable<any> {
    let url = this.adminTagsUrl;
    const params: string[] = [];
    if (search) {
      params.push(`search=${encodeURIComponent(search)}`);
    }
    if (perPage) {
      params.push(`per_page=${perPage}`);
    }
    if (params.length) {
      url += `?${params.join('&')}`;
    }
    return this.http.get<any>(url);
  }

  createTag(name: string): Observable<any> {
    return this.http.post<any>(this.adminTagsUrl, { name });
  }

  updateTag(id: number, name: string): Observable<any> {
    return this.http.put<any>(`${this.adminTagsUrl}/${id}`, { name });
  }

  deleteTag(id: number): Observable<any> {
    return this.http.delete<any>(`${this.adminTagsUrl}/${id}`);
  }

  bulkDeleteTags(ids: number[]): Observable<any> {
    return this.http.post<any>(`${this.adminTagsUrl}/bulk-delete`, { ids });
  }
}
